import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCheck, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function StoreNotifications() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = () => api.get("/store/notifications").then((r) => setItems(r.data));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);
  const markRead = async () => { await api.post("/store/notifications/mark-read"); toast.success("Marked as read"); load(); };
  const dismiss = async (id) => {
    setBusyId(id);
    // Optimistic remove
    setItems((prev) => prev.filter((n) => n.id !== id));
    try {
      await api.post(`/store/notifications/${id}/dismiss`);
    } catch (e) {
      toast.error("Failed to remove");
      load();
    } finally { setBusyId(null); }
  };
  const clearRead = async () => {
    try {
      const r = await api.post(`/store/notifications/clear-read`);
      toast.success(`Cleared ${r.data.deleted || 0} read notifications`);
      load();
    } catch (_) { toast.error("Failed to clear"); }
  };
  const unread = items.filter((i) => i.status === "unread").length;
  const readCount = items.filter((i) => i.status !== "unread").length;
  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="store-notifications-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">Notifications {unread > 0 && <Badge className="bg-orange-500" data-testid="unread-badge">{unread} new</Badge>}</h1>
          <p className="text-slate-500">Every new order, live. Mark done to clear it from the tab.</p>
        </div>
        <div className="flex gap-2">
          {unread > 0 && <Button variant="outline" onClick={markRead} data-testid="mark-all-read"><CheckCheck className="w-4 h-4 mr-2" />Mark all read</Button>}
          {readCount > 0 && <Button variant="outline" onClick={clearRead} data-testid="clear-read"><Trash2 className="w-4 h-4 mr-2" />Clear read</Button>}
        </div>
      </div>
      <Card className="border-slate-200"><CardContent className="p-0 divide-y divide-slate-100">
        {items.map((n) => (
          <div key={n.id} className={`p-4 flex items-start gap-3 ${n.status === "unread" ? "bg-orange-50/40" : ""}`} data-testid={`notif-${n.id}`}>
            <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 grid place-items-center shrink-0"><Bell className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-sm break-words">{n.message || n.body || n.title}</div>
              <div className="text-xs text-slate-400 font-mono mt-1">{(n.created_at || "").slice(0, 19).replace("T", " ")}</div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              disabled={busyId === n.id}
              onClick={() => dismiss(n.id)}
              className="shrink-0 text-slate-400 hover:text-red-600 hover:bg-red-50 h-8 px-2"
              data-testid={`dismiss-${n.id}`}
              title="Mark done"
            >
              <X className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline text-xs">Mark done</span>
            </Button>
          </div>
        ))}
        {!items.length && <div className="p-8 text-center text-slate-400" data-testid="no-notifs">No notifications yet</div>}
      </CardContent></Card>
    </div>
  );
}
